import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { allProducts } from '../../mocks/products';
import { getProductReviews, getAverageRating, getReviewCount } from '../../mocks/reviews';

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState('description');
  const [addedToCart, setAddedToCart] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const [expandedReview, setExpandedReview] = useState<number | null>(null);
  const [reviewFilter, setReviewFilter] = useState<number | null>(null);
  const [helpfulReviews, setHelpfulReviews] = useState<Record<number, number>>({});
  const [markedHelpful, setMarkedHelpful] = useState<Set<number>>(new Set());
  const reviewsRef = useRef<HTMLDivElement>(null);

  const product = allProducts.find(p => p.id === Number(productId));
  const productReviews = product ? getProductReviews(product.id) : [];
  const averageRating = product ? getAverageRating(product.id) : 0;
  const reviewCount = product ? getReviewCount(product.id) : 0;

  useEffect(() => {
    window.scrollTo(0, 0);
    // Initialize helpful counts from reviews
    const initialCounts: Record<number, number> = {};
    productReviews.forEach(review => {
      initialCounts[review.id] = review.helpful;
    });
    setHelpfulReviews(initialCounts);
  }, [productId]);

  if (!product) {
    return (
      <div className="min-h-screen bg-[#F8F6F3]">
        <Header />
        <div className="pt-32 pb-20 text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Product Not Found</h1>
          <Link to="/products" className="text-[#FF6B6B] hover:underline">
            Back to Products
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const productImages = (product as any).images || [product.image];

  const filteredReviews = reviewFilter 
    ? productReviews.filter(r => r.rating === reviewFilter)
    : productReviews;

  const ratingDistribution = [5, 4, 3, 2, 1].map(rating => ({
    rating,
    count: productReviews.filter(r => r.rating === rating).length,
    percentage: productReviews.length > 0 
      ? (productReviews.filter(r => r.rating === rating).length / productReviews.length) * 100 
      : 0
  }));

  const handleAddToCart = () => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: any) => item.id === product.id);
    
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.image,
        quantity: quantity
      });
    }
    
    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  const relatedProducts = allProducts
    .filter(p => p.category === product.category && p.id !== product.id)
    .slice(0, 4);

  const productDetails = {
    42: {
      description: `The 2025 TEC PlayStation 5 Disc Slim Edition represents the pinnacle of next-generation gaming. This sleek, redesigned console delivers breathtaking 4K Ultra HD visuals and lightning-fast load times thanks to its custom ultra-fast SSD.

Experience gaming like never before with stunning ray-tracing technology, immersive 3D audio, and the revolutionary DualSense wireless controller with haptic feedback. The slim design takes up 30% less space than the original PS5 while maintaining all the power you expect.

With the disc drive included, you have access to your entire physical game library plus the ability to play 4K Blu-ray movies. The sleek white and black design fits perfectly into any entertainment setup, making it both a powerful gaming machine and a stylish centerpiece.`,
      features: [
        'Custom AMD Zen 2 CPU with 8 cores at 3.5GHz',
        'Custom AMD RDNA 2 GPU with 10.28 TFLOPs',
        '16GB GDDR6 RAM with 256-bit interface',
        '1TB Custom Ultra-Fast NVMe SSD',
        '4K Ultra HD Blu-ray Disc Drive',
        'Up to 120fps with 4K output support',
        'Hardware-accelerated Ray Tracing',
        'Tempest 3D AudioTech for immersive sound',
        'DualSense Wireless Controller included',
        'Haptic Feedback and Adaptive Triggers',
        'USB Type-A and USB Type-C ports',
        'Wi-Fi 6 and Bluetooth 5.1 connectivity',
        '30% slimmer design than original PS5',
        'Backward compatible with PS4 games'
      ],
      warranty: '1-Year Manufacturer Warranty with Sony Support'
    },
    41: {
      description: `The HP 15 Pentium Laptop in stunning Scarlet Red is the perfect companion for everyday computing. Whether you're browsing the web, streaming your favorite shows, or tackling school assignments, this laptop delivers reliable performance in a stylish package.

Featuring a vibrant 15.6-inch HD display, you'll enjoy crisp visuals for all your entertainment and productivity needs. The Intel Pentium N5030 processor provides smooth multitasking capabilities, while 4GB of RAM ensures responsive performance for daily tasks.

With 128GB of SSD storage, you'll experience faster boot times and quick file access compared to traditional hard drives. The sleek scarlet red finish makes a bold statement, setting you apart from the crowd.`,
      features: [
        'Intel Pentium Silver N5030 Quad-Core Processor (1.1GHz base, up to 3.1GHz)',
        '15.6-inch HD (1366 x 768) Anti-Glare Display',
        '4GB DDR4-2400 SDRAM Memory',
        '128GB PCIe NVMe M.2 Solid State Drive',
        'Intel UHD Graphics 605',
        'HP True Vision 720p HD Camera',
        'Dual Speakers with Audio by B&O',
        'Full-size Island-style Keyboard',
        'Multi-touch Gesture Support Touchpad',
        'Wi-Fi 5 (802.11ac) and Bluetooth 4.2',
        '41Wh Li-ion Battery (Up to 11 hours)',
        'Windows 11 Home in S Mode',
        'USB Type-C, 2x USB Type-A, HDMI, SD Card Reader',
        'Weight: 3.75 lbs (1.7 kg)'
      ],
      warranty: '1-Year Limited Hardware Warranty with 24/7 Support'
    }
  };

  const details = productDetails[product.id as keyof typeof productDetails] || {
    description: `Experience premium quality with the ${product.name}. This ${product.category.toLowerCase()} delivers exceptional performance and reliability for all your needs. Built with cutting-edge technology and designed for modern users.`,
    features: product.specs.split(', ').map(spec => spec.trim()),
    warranty: '1-Year Limited Hardware Warranty'
  };

  const handleScrollToReviews = () => {
    setActiveTab('reviews');
    setTimeout(() => {
      reviewsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleHelpfulClick = (reviewId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (markedHelpful.has(reviewId)) {
      // Already marked, so unmark
      setHelpfulReviews(prev => ({
        ...prev,
        [reviewId]: prev[reviewId] - 1
      }));
      setMarkedHelpful(prev => {
        const newSet = new Set(prev);
        newSet.delete(reviewId);
        return newSet;
      });
    } else {
      // Mark as helpful
      setHelpfulReviews(prev => ({
        ...prev,
        [reviewId]: prev[reviewId] + 1
      }));
      setMarkedHelpful(prev => new Set(prev).add(reviewId));
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F6F3]">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 bg-gradient-to-br from-[#2C3E50] to-[#34495E]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <nav className="flex items-center gap-2 text-sm text-white/70">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/products" className="hover:text-white transition-colors cursor-pointer">Products</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/products?category=${encodeURIComponent(product.category)}`} className="hover:text-white transition-colors cursor-pointer">{product.category}</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Product Section */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="grid grid-cols-2 gap-0">
              {/* Product Images */}
              <div className="p-8 bg-gray-50">
                <div className="relative w-full h-[500px] rounded-xl overflow-hidden mb-4">
                  <img
                    src={productImages[selectedImage]}
                    alt={product.name}
                    className="w-full h-full object-contain"
                  />
                  {product.inStock && (
                    <span className="absolute top-4 left-4 bg-green-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                      In Stock
                    </span>
                  )}
                </div>
                <div className="flex gap-3 justify-center flex-wrap">
                  {productImages.map((img: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                        selectedImage === index ? 'border-[#FF6B6B]' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <img src={img} alt={`View ${index + 1}`} className="w-full h-full object-contain" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Product Info */}
              <div className="p-8">
                <div className="mb-2">
                  <span className="text-sm font-medium text-[#FF6B6B] uppercase tracking-wider">
                    {product.category}
                  </span>
                </div>
                
                <h1 className="text-3xl font-bold text-gray-900 mb-4">{product.name}</h1>
                
                <div className="flex items-center gap-4 mb-6">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <i
                        key={i}
                        className={`ri-star-fill text-lg ${
                          i < Math.floor(averageRating) ? 'text-amber-400' : 'text-gray-200'
                        }`}
                      ></i>
                    ))}
                  </div>
                  <span className="text-sm text-gray-600">{averageRating} out of 5</span>
                  <span className="text-sm text-gray-400">|</span>
                  <button 
                    onClick={handleScrollToReviews}
                    className="text-sm text-[#FF6B6B] hover:underline cursor-pointer"
                  >
                    {reviewCount} Reviews
                  </button>
                </div>

                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">${product.price.toFixed(2)}</span>
                  <span className="text-sm text-gray-500 ml-2">Free Shipping</span>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed">
                  {product.specs}
                </p>

                <div className="border-t border-gray-100 pt-6 mb-6">
                  <div className="flex items-center gap-6 mb-6">
                    <span className="text-sm font-medium text-gray-700">Quantity:</span>
                    <div className="flex items-center border border-gray-200 rounded-lg">
                      <button
                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                        className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-subtract-line"></i>
                      </button>
                      <span className="w-12 text-center font-medium">{quantity}</span>
                      <button
                        onClick={() => setQuantity(quantity + 1)}
                        className="w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer"
                      >
                        <i className="ri-add-line"></i>
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={handleAddToCart}
                      className="flex-1 bg-[#FF6B6B] hover:bg-[#FF5252] text-white font-semibold py-4 px-8 rounded-lg transition-all flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                    >
                      {addedToCart ? (
                        <>
                          <i className="ri-check-line text-xl"></i>
                          Added to Cart!
                        </>
                      ) : (
                        <>
                          <i className="ri-shopping-cart-line text-xl"></i>
                          Add to Cart
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        handleAddToCart();
                        setTimeout(() => navigate('/cart'), 300);
                      }}
                      className="flex-1 bg-gray-900 hover:bg-gray-800 text-white font-semibold py-4 px-8 rounded-lg transition-all cursor-pointer whitespace-nowrap"
                    >
                      Buy Now
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#FF6B6B]/10 rounded-lg">
                      <i className="ri-truck-line text-[#FF6B6B] text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900">Free Shipping</p>
                      <p className="text-xs text-gray-500">Orders over $50</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#4ECDC4]/10 rounded-lg">
                      <i className="ri-shield-check-line text-[#4ECDC4] text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900">1 Year Warranty</p>
                      <p className="text-xs text-gray-500">Full coverage</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#F4A261]/10 rounded-lg">
                      <i className="ri-refresh-line text-[#F4A261] text-xl"></i>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-gray-900">30-Day Returns</p>
                      <p className="text-xs text-gray-500">Easy returns</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details Tabs */}
      <section className="py-12" ref={reviewsRef}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
            <div className="border-b border-gray-100">
              <div className="flex">
                {['description', 'features', 'reviews', 'warranty'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`px-8 py-4 text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      activeTab === tab
                        ? 'text-[#FF6B6B] border-b-2 border-[#FF6B6B]'
                        : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    {tab === 'reviews' ? `Reviews (${reviewCount})` : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="p-8">
              {activeTab === 'description' && (
                <div className="prose max-w-none">
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {details.description}
                  </p>
                </div>
              )}
              
              {activeTab === 'features' && (
                <ul className="grid grid-cols-2 gap-4">
                  {details.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-[#4ECDC4] text-lg mt-0.5"></i>
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
              )}

              {activeTab === 'reviews' && (
                <div>
                  {/* Reviews Summary */}
                  <div className="flex gap-12 mb-8 pb-8 border-b border-gray-100">
                    <div className="text-center min-w-[120px]">
                      <div className="text-5xl font-bold text-gray-900 mb-2">{averageRating}</div>
                      <div className="flex items-center justify-center gap-1 mb-2">
                        {[...Array(5)].map((_, i) => (
                          <i
                            key={i}
                            className={`ri-star-fill text-lg ${
                              i < Math.floor(averageRating) ? 'text-amber-400' : 'text-gray-200'
                            }`}
                          ></i>
                        ))}
                      </div>
                      <p className="text-sm text-gray-500">{reviewCount} reviews</p>
                    </div>
                    
                    <div className="flex-1 max-w-md">
                      {ratingDistribution.map(({ rating, count, percentage }) => (
                        <button
                          key={rating}
                          onClick={() => setReviewFilter(reviewFilter === rating ? null : rating)}
                          className={`flex items-center gap-3 w-full py-1.5 cursor-pointer hover:bg-gray-50 rounded px-2 transition-colors ${
                            reviewFilter === rating ? 'bg-amber-50' : ''
                          }`}
                        >
                          <span className="text-sm text-gray-600 w-14">{rating} star</span>
                          <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-amber-400 rounded-full transition-all"
                              style={{ width: `${percentage}%` }}
                            ></div>
                          </div>
                          <span className="text-sm text-gray-500 w-8 text-right">{count}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Filter indicator */}
                  {reviewFilter && (
                    <div className="flex items-center gap-2 mb-6 p-3 bg-amber-50 rounded-lg">
                      <i className="ri-filter-3-line text-amber-600"></i>
                      <span className="text-sm text-gray-700">Showing {filteredReviews.length} reviews with {reviewFilter} stars</span>
                      <button 
                        onClick={() => setReviewFilter(null)}
                        className="ml-auto text-sm text-[#FF6B6B] hover:underline cursor-pointer flex items-center gap-1"
                      >
                        <i className="ri-close-line"></i>
                        Clear filter
                      </button>
                    </div>
                  )}

                  {/* Reviews List */}
                  <div className="space-y-4">
                    {filteredReviews.map((review) => (
                      <div 
                        key={review.id} 
                        className={`border rounded-xl p-6 transition-all cursor-pointer ${
                          expandedReview === review.id 
                            ? 'border-[#FF6B6B]/30 bg-[#FF6B6B]/5 shadow-sm' 
                            : 'border-gray-100 hover:border-gray-200 hover:shadow-sm'
                        }`}
                        onClick={() => setExpandedReview(expandedReview === review.id ? null : review.id)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#FF8E8E] flex items-center justify-center text-white text-sm font-semibold">
                                {review.author.charAt(0)}
                              </div>
                              <span className="font-semibold text-gray-900">{review.author}</span>
                              {review.verified && (
                                <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                                  <i className="ri-checkbox-circle-fill"></i>
                                  Verified
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-3 ml-10">
                              <div className="flex items-center gap-0.5">
                                {[...Array(5)].map((_, i) => (
                                  <i
                                    key={i}
                                    className={`ri-star-fill text-sm ${
                                      i < review.rating ? 'text-amber-400' : 'text-gray-200'
                                    }`}
                                  ></i>
                                ))}
                              </div>
                              <span className="text-xs text-gray-400">
                                {new Date(review.date).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short', 
                                  day: 'numeric' 
                                })}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">Click to {expandedReview === review.id ? 'collapse' : 'expand'}</span>
                            <i className={`ri-arrow-${expandedReview === review.id ? 'up' : 'down'}-s-line text-gray-400 text-lg`}></i>
                          </div>
                        </div>
                        
                        <h4 className="font-medium text-gray-900 mb-2 ml-10">{review.title}</h4>
                        
                        <p className={`text-gray-600 text-sm leading-relaxed ml-10 ${
                          expandedReview === review.id ? '' : 'line-clamp-2'
                        }`}>
                          {review.content}
                        </p>
                        
                        {expandedReview === review.id && (
                          <div className="flex items-center gap-6 mt-4 pt-4 border-t border-gray-100 ml-10">
                            <button 
                              onClick={(e) => handleHelpfulClick(review.id, e)}
                              className={`flex items-center gap-2 text-sm transition-colors cursor-pointer ${
                                markedHelpful.has(review.id)
                                  ? 'text-[#FF6B6B] font-medium'
                                  : 'text-gray-500 hover:text-[#FF6B6B]'
                              }`}
                            >
                              <i className={markedHelpful.has(review.id) ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'}></i>
                              Helpful ({helpfulReviews[review.id] || 0})
                            </button>
                            <button 
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
                            >
                              <i className="ri-flag-line"></i>
                              Report
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {filteredReviews.length === 0 && (
                    <div className="text-center py-12">
                      <i className="ri-chat-3-line text-5xl text-gray-300 mb-4 block"></i>
                      <p className="text-gray-500 mb-2">No reviews match your filter</p>
                      <button 
                        onClick={() => setReviewFilter(null)}
                        className="text-sm text-[#FF6B6B] hover:underline cursor-pointer"
                      >
                        Show all reviews
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'warranty' && (
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#4ECDC4]/10 rounded-xl">
                    <i className="ri-shield-check-line text-[#4ECDC4] text-2xl"></i>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Warranty Information</h3>
                    <p className="text-gray-600">{details.warranty}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Products</h2>
            <div className="grid grid-cols-4 gap-6" data-product-shop>
              {relatedProducts.map((relatedProduct) => (
                <Link
                  key={relatedProduct.id}
                  to={`/product/${relatedProduct.id}`}
                  className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all group cursor-pointer"
                >
                  <div className="relative w-full h-48 overflow-hidden">
                    <img
                      src={relatedProduct.image}
                      alt={relatedProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-xs text-[#FF6B6B] font-medium mb-1">{relatedProduct.category}</p>
                    <h3 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">{relatedProduct.name}</h3>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-gray-900">${relatedProduct.price.toFixed(2)}</span>
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-amber-400 text-sm"></i>
                        <span className="text-xs text-gray-500">{relatedProduct.rating}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
